import Data from 'data';
import { defaultSummon } from 'logic/monster/summons';
import { skillAuraStats, skillCalc, skillFormula } from 'logic/skills/calc';
import { AnimationCalculator, RollbackAnimationCalculator } from '../animation';
import { DamageCalculator, missileDamage } from '../damage';
import { DPSCalculator } from '../dps';

const functions = {};

// Guided Arrow, Bone Spirit

functions[10] = {
  damage(skill, stats, weapon, level, target) {
    const missile = Data.missileByName[skill.srvmissilea];
    if (!missile) return;
    return missileDamage(missile, stats, weapon, level, target, {stats: {damagepercent: skillFormula(skill, 'clc1', stats, level)}});
  },
};

// Charged Strike

functions[11] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(128);

    const damage = {...missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target)};
    if (damage.damage) {
      damage.bolt = damage.damage;
      damage.bolt.count = skillFormula(skill, 'clc1', stats, level);
    }
    damage.damage = calc.calculate(target);
    return damage;
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info?.animation) return;
    const calc = new DPSCalculator();
    calc.basic_damage('damage', info.damage.damage, info.animation);
    calc.basic_damage('bolt', info.damage.bolt, info.animation, {count: target.size === 1 ? 0 : info.damage.bolt.count});
    return calc.calculate(stats, target);
  },
};

// Strafe

functions[12] = {
  damage(skill, stats, weapon, level, target) {
    const missile = Data.missileByName[skill.srvmissilea];
    if (!missile) return;
    return missileDamage(missile, stats, stats.weapon1, level, target, {
      stats: {
        damagepercent: skillFormula(skill, 'clc2', stats, level),
        tohit_percent: skillFormula(skill, 'toht', stats, level),
      },
    });
  },
  animation(skill, stats, level) {
    const attacks = Math.min(skillFormula(skill, 'clc1', stats, level), skillFormula(skill, 'clc3', stats, level));
    const animation = new RollbackAnimationCalculator(stats, skill, attacks, skill.param6 || 0);
    animation.maxaccel = 149;
    return animation.calculate();
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info?.animation) return;
    const skill = Data.skills[id];
    const missile = Data.missileByName[skill.srvmissilea];
    if (!missile) return;
    const delay = missile.nextdelay;
    let hits = 0, lasthit = -100, time = 0;
    const anim = info.animation;
    for (let i = 0; i < anim.hits; ++i) {
      const frames = anim.result[i === anim.hits - 1 ? i - 1 : i];
      time += frames;
      const next = Math.max(time, lasthit + delay + 1);
      if (next < time + target.size) {
        hits += 1;
        lasthit = next;
      }
    }
    const calc = new DPSCalculator();
    calc.basic_damage('damage', info.damage.damage, info.animation, {count: hits});
    return calc.calculate(stats, target);
  },
};

// Fend, Zeal, Fury

functions[13] = {
  damage(skill, stats, weapon, level, target) {
    if (!weapon) return null;
    const calc = new DamageCalculator(stats, weapon, false, true);
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc2', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(skill.srcdam);
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
  animation(skill, stats, level) {
    const hits = skill.srvstfunc === 9 ? 1 : skillFormula(skill, 'clc1', stats, level);
    const anim = (hits === 1 ?
      new AnimationCalculator(stats, skill) :
      new RollbackAnimationCalculator(stats, skill, hits, skill.param2 || 0)
    );
    if (!anim.type) return; // no scaling - pointless to display
    return anim.calculate();
  },
};

// Lightning Strike

functions[14] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, true);
    calc.elemental('ltng', skillFormula(skill, 'enms', stats, level),
      skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.srcdam(128);

    const damage = {...missileDamage(Data.missileByName[skill.srvmissilea], stats, stats.weapon1, level, target)};
    damage.chain = damage.damage;
    damage.damage = calc.calculate(target);

    return damage;
  },
};

// Valkyrie

functions[16] = {
  summon(id, stats, level, target) {
    return defaultSummon(id, stats, level, target, {
      itemLevel: skillFormula(Data.skills[id], 'clc2', stats, level),
    });
  },
};

// Charged Bolt

functions[17] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target);
  },
  dps(id, stats, level, target, info) {
    if (!info?.damage || !info?.animation) return;
    const skill = Data.skills[id];
    const calc = new DPSCalculator();
    calc.basic_damage('damage', info.damage.damage, info.animation, {count: skillFormula(skill, 'clc1', stats, level)});
    return calc.calculate(stats, target);
  },
};

// Self buffs

functions[18] = {
  buff(id, stats, level) {
    const skill = Data.skills[id];
    if (skill.srvstfunc === 36) {
      // Holy Shield hardcode
      return {
        level,
        state: skill.aurastate,
        stats: () => ({
          [skill.aurastat1]: skillCalc(skill, skill.aurastatcalc1, stats, level),
          skill_armor_percent: skillCalc(skill, skill.aurastatcalc2, stats, level),
          smite_mindamage: skillFormula(skill, 'pdmn', stats, level),
          smite_maxdamage: skillFormula(skill, 'pdmx', stats, level),
        }),
      };
    }
    return {
      level,
      state: skill.aurastate,
      stats: () => skillAuraStats(skill, stats, level),
    };
  },
};

// Inferno, Arctic Blast

functions[19] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target, {frames: -2});
  },
  animation() {},
  dps: () => null,
};

export default functions;
